import { ConvLogSchema } from '../../../services/logging/schemas/conversation.log-schema';
import { Injectable, OnDestroy } from '@angular/core';
import { from, Observable, retry, Subscription, timer } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';

import { TBotItem } from '@core/interfaces/TBotItem';

import { LogService } from '../../../services/logging/log.service';
import { DynamicStatusService } from '../components/partials/dynamic-status/dynamic-status.service';
import { Conversation } from '../infrastructure/models/Conversation';
import { IInputTransferData } from '../types/dtos';
import { AgentStrategyManagerService } from './agents-strategies/agent-strategy-manager.service';
import { ConversationDataService } from './conversation-data.service';
import { BotsService } from '@shared/services/bots.service';

import { AgentStrategyInterface } from './agents-strategies/agent-strategy.interface';

@Injectable({
  providedIn: 'root',
})
export class AgentConversationService implements OnDestroy {
  private _agent: TBotItem | null = null;
  private currentSub?: Subscription;
  private lastFailedRequest: IInputTransferData | null = null;
  private botSubscription?: Subscription;

  private readonly MAX_ATTEMPTS = 3;
  private readonly RETRY_DELAY = 500;
  private currentAttempt = 0;
  private convLogs = this._logger.createBuilder(ConvLogSchema);

  constructor(
    private _conversationDataService: ConversationDataService,
    private _dynamicStatus: DynamicStatusService,
    private _agentStrategyManager: AgentStrategyManagerService,
    private _logger: LogService,
    private _botsService: BotsService
  ) {
    this.initializeBotSubscription();
  }

  private initializeBotSubscription() {
    // Subscribe to bot updates to enhance conversation data
    this.botSubscription = this._botsService
      .getCurrentBotObservable()
      .subscribe((bot: TBotItem | null) => {
        if (bot) {
          const currentConversation = this._conversationDataService.currentValue;
          if (currentConversation?.bot?._id === bot._id) {
            // Update the conversation's bot with complete information
            currentConversation.bot = { ...currentConversation.bot, ...bot };
            this._conversationDataService.emitNext(currentConversation);
          }
        }
      });
  }

  newConversation(agent?: TBotItem) {
    this._agent = agent || null;
    this._conversationDataService.reset();
    this.abortRequests();
    // Clear any stored failed request when starting a new conversation
    this.lastFailedRequest = null;
  }

  async askQuestionToAgent(message: IInputTransferData, appendMessage = false) {
    this.currentAttempt = 0;
    const conversation = this.getCurrentConversation();
    const strategy = await this._agentStrategyManager.setStrategy(conversation.bot);

    this.convLogs.setValue('conversation', conversation).setValue('bodyRequest', message);

    // Add user message and empty assistant message first
    if (!appendMessage) {
      // Set keywords from frontend selection for immediate display
      const userMsg = conversation.addNewMessage('user', message.message);
      userMsg.keywords = message.keywords || [];
      
      // Set files from the message if they exist (temporary display until server responds with _id)
      if (message.files && message.files.length > 0) {
        userMsg.files = message.files.map(file => ({
          name: file.name,
          originalName: file.name,
          _id: '', // Will be populated by server response
        }));
      }
      
      // Set reference files if they exist (temporary display until server responds with _id)
      if (message.referenceFiles && message.referenceFiles.length > 0) {
        userMsg.referenceFiles = message.referenceFiles.map(file => ({
          name: file.name,
          originalName: file.name,
          _id: '', // Will be populated by server response
        }));
      }
      
      conversation.addNewMessage('assistant', '');
    }
    this._conversationDataService.emitNext(conversation);

    const request$ = this._conversationDataService.conversationExists()
      ? this.askAnotherQuestion(strategy, conversation, message)
      : this.initConversationFlow(strategy, message, conversation);

    this.currentSub = request$
      .pipe(
        retry({
          count: this.MAX_ATTEMPTS,
          delay: () => {
            this.currentAttempt++;
            this.convLogs.setValue('attempts', this.currentAttempt);
            const currentDelay = this.currentAttempt * this.RETRY_DELAY;
            console.warn(
              `Conversation failed attempt ${this.currentAttempt} in next ${currentDelay / 1000}s...`
            );
            return timer(currentDelay);
          },
          resetOnSuccess: true,
        }),
        finalize(() => this._conversationDataService.emitWritingMessageFinished())
      )
      .subscribe({
        next: (update: Conversation) => this.handleUpdate(update),
        error: (error: any) => {
          this.storeFailedRequest(message);
          this.handleError(error);
        },
        complete: () => this.handleComplete(),
      });
  }

  private askAnotherQuestion(
    strategy: AgentStrategyInterface,
    conversation: Conversation,
    message: IInputTransferData
  ) {
    return strategy.sendAnotherQuestion(conversation, message).pipe(
      finalize(() => {
        this.convLogs
          .setValue('scope', 'add question')
          .setValue('status', 'init')
          .setValue('bodyRequest', message)
          .log();
      })
    );
  }

  private initConversationFlow(
    initialStrategy: AgentStrategyInterface,
    message: IInputTransferData,
    conversation: Conversation
  ): Observable<Conversation> {
    return new Observable<Conversation>(observer => {
      const initial$ = initialStrategy.initConversation(conversation, message);
      const initSub = initial$
        .pipe(
          finalize(() => {
            this.convLogs.log();
          })
        )
        .subscribe({
          next: conv => observer.next(conv),
          error: err => observer.error(err),
          complete: () => {
            from(this._agentStrategyManager.setStrategy(conversation.bot!))
              .pipe(
                finalize(() => {
                  this.convLogs.setValue('scope', 'after init').log();
                }),
                switchMap(() =>
                  this._agentStrategyManager.strategy.afterInitConversation(conversation, message)
                )
              )
              .subscribe({
                next: conv2 => observer.next(conv2),
                error: err2 => observer.error(err2),
                complete: () => observer.complete(),
              });
          },
        });
      return () => {
        initSub.unsubscribe();
      };
    });
  }

  protected handleUpdate(conversation: Conversation) {
    this._conversationDataService.emitNext(conversation);
  }

  protected handleError(error: any) {
    this.convLogs.setError(error);
    // Log error for debugging but don't show technical details to user
    console.debug('Conversation request failed, showing retry option to user');
    this._dynamicStatus.clear();

    // Small delay to make the transition smoother
    setTimeout(() => {
      this.addRetryMessage();
    }, 500);
  }

  protected handleComplete() {
    // Clear failed request on successful completion
    this.lastFailedRequest = null;

    if (this.currentSub) {
      this.currentSub.unsubscribe();
      this.currentSub = undefined;
    }
  }

  private getCurrentConversation() {
    return (
      this._conversationDataService.currentValue ??
      new Conversation({ bot: this._agent!, type: 'conversation' })
    );
  }

  addRetryMessage() {
    const conversation = this.getCurrentConversation();
    this._conversationDataService.toggleIsBusy(false);
    this._conversationDataService.toggleIsWriting(false);
    conversation.addNewRetryMessage();
    this._conversationDataService.emitNext(conversation);
  }

  retryLastRequest() {
    if (this.lastFailedRequest) {
      // Remove the retry message first
      this.removeLastErrorMessage();

      // Reset the conversation and start fresh
      this.resetConversationAndRetry(this.lastFailedRequest);
    }
  }

  private resetConversationAndRetry(message: IInputTransferData) {
    // Clear current conversation state
    this._conversationDataService.reset();

    // Reset conversation ID to force new conversation
    const conversation = this.getCurrentConversation();
    conversation.id = undefined;

    // Clear any existing messages
    conversation.messages = [];

    // Start fresh conversation with the original message
    this.askQuestionToAgent(message, false);
  }

  private removeLastErrorMessage() {
    const conversation = this.getCurrentConversation();
    const lastMessage = conversation.getLastMessage();
    if (lastMessage && lastMessage.type === 'retry') {
      conversation.messages.pop();
      this._conversationDataService.emitNext(conversation);
    }
  }

  private storeFailedRequest(message: IInputTransferData) {
    // Create a deep copy of the message to preserve all data
    this.lastFailedRequest = {
      message: message.message,
      files: message.files ? [...message.files] : undefined,
      sourceIds: message.sourceIds ? [...message.sourceIds] : undefined,
      request_type: message.request_type,
      project_id: message.project_id,
      language: message.language,
      language_name: message.language_name,
      language_code: message.language_code,
      dateStart: message.dateStart,
      dateEnd: message.dateEnd,
      countries: message.countries ? [...message.countries] : undefined,
    };
  }

  abortRequests() {
    this._dynamicStatus.clear();
    this._conversationDataService.toggleIsBusy(false);
    this._conversationDataService.toggleIsWriting(false);
    this._agentStrategyManager.getStrategyOrNull()?.cancel();
  }

  ngOnDestroy() {
    if (this.botSubscription) {
      this.botSubscription.unsubscribe();
    }
  }
}
